import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

export type PoLine = {
  po_number: string;
  vendor?: string | null;
  tab?: string | null;
  desc?: string | null;
  line_count?: number | null;
  email_url?: string | null;
};

export type PoFlag = {
  id: string;
  severity?: string;
  message: string;
  resolved?: boolean;
  email_url?: string | null;
};

export type PoRun = {
  id: string;
  run_label: string;
  ran_at: string;
  po_low: number | null;
  po_high: number | null;
  new_count: number;
  revision_count: number;
  archived_count: number;
  rows_filed: number;
  status: string;
  pos: PoLine[];
  flags: PoFlag[];
  notes: string | null;
  created_at: string;
};

/** List PO organizer runs, newest first. */
export function usePoRuns() {
  const api = useApi();
  return useQuery({
    queryKey: ["po_organizer_runs"],
    queryFn: async (): Promise<PoRun[]> => {
      const res = await api(`/api/po_organizer_runs`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as PoRun[];
    },
    staleTime: 30_000,
  });
}

/** Mark a single run flag resolved (or un-resolved). */
export function useResolveFlag() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      runId,
      flagId,
      resolved = true,
    }: {
      runId: string;
      flagId: string;
      resolved?: boolean;
    }): Promise<PoRun> => {
      const res = await api(`/api/po_organizer_runs/${runId}`, {
        method: "PATCH",
        body: JSON.stringify({ resolve_flag_id: flagId, resolved }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
        throw new Error(j.detail || j.error || `HTTP ${res.status}`);
      }
      return (await res.json()) as PoRun;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["po_organizer_runs"] });
    },
  });
}
